/* eslint-disable no-underscore-dangle */
import { DateSelectArg, DatesSetArg, EventClickArg, EventSourceInput } from '@fullcalendar/core';
import FullCalendar from '@fullcalendar/react';
import { ResourceApi, ResourceSourceInput } from '@fullcalendar/resource';
import { Box, Button, PopoverPosition } from '@mui/material';
import { useOperatorsScheduleDriversQuery } from 'api/queries/operators';
import { useUsersCurrentAccessesQuery } from 'api/queries/users';
import { ScheduleQueryParams } from 'api/types';
import DriverCreateModalForm from 'components/forms/DriverFormFactory/DriverCreateModalForm';
import dayjs from 'dayjs';
import { ScheduleDriver, ScheduleItemType, UserPermission } from 'remote/global';
import { useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { AddEventTypeEnum, SchedulerTypeEnum } from 'utils/data/data-types';
import { ROUTES } from 'utils/data/routes';
import { formatDateWithTz, hasWritePermission } from 'utils/helpers';
import AddEventDropdown, { AddEventDropdownList } from 'components/scheduler/AddEventDropdown';
import SchedulerPageHeader from 'components/scheduler/SchedulerPageHeader';
import { SchedulerOverlay } from 'utils/component-utils';
import WrappedOrderDetailsModal from 'components/modals/OrderModal/WrappedOrderDetailsModal';
import CustomFullCalendar from 'components/scheduler/CustomFullCalendar';
import DriverVacationModal from 'components/modals/DriverVacationModal';
import { DEFAULT_VACATION_TYPE } from 'utils/constants';

type SelectedRange = {
  position?: PopoverPosition;
  data: {
    start?: Date;
    end?: Date;
  };
};

function DriverSchedulerView() {
  const [isDriverModalOpen, setIsDriverModalOpen] = useState(false);
  const [isVacationModalOpen, setIsVacationModalOpen] = useState(false);
  const [selectedDateString, setSelectedDateString] = useState('');
  const [selectedRange, setSelectedRange] = useState<SelectedRange | undefined>();
  const [selectedCellInfo, setSelectedCellInfo] = useState<DateSelectArg | undefined>();
  const [isVacationEditMode, setIsVacationEditMode] = useState(false);
  const [isEventDropdownListOpen, setIsEventDropdownListOpen] = useState(false);
  const [isOrderDetailsModalOpen, setIsOrderDetailsModalOpen] = useState(false);
  const { t } = useTranslation();
  const calendarRef = useRef<FullCalendar>(null);
  const [bookingItem, setBookingItem] = useState<
    { bookingNumber: string; rideId: number } | undefined
  >();
  const [queryParams, setQueryParams] = useState<ScheduleQueryParams | undefined>();
  const { data: schedule, isLoading } = useOperatorsScheduleDriversQuery(queryParams);
  const isDataLoading = isLoading;
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const { permissions = {} } = userInfo || {};

  const eventSource = useMemo(() => {
    const getDriver = (ownerId: number) => {
      return schedule?.drivers?.find((d) => d.id === ownerId);
    };

    return schedule?.scheduleItems?.map((event) => {
      const driver = getDriver(event.ownerId);
      return {
        ...driver,
        ...event,
        resourceId: event.ownerId,
        title: event.bookingNumber || '',
        start: event.startTime,
        end: event.endTime,
        orderType: event.orderType,
        rideId: event.rideId,
        rideNumberTranslated: event.rideNumber
          ? t('terms.attributes.ride.rideNo', { rideNo: event.rideNumber })
          : '',
        itemTypeTranslated: t(`ScheduleItemType.${event.itemType}`),
      };
    });
  }, [schedule?.scheduleItems, t, schedule?.drivers]);

  const elementSource = useMemo(() => {
    return (schedule?.drivers ?? []).map((driver: ScheduleDriver) => ({
      ...driver,
      title: `${driver.firstName} ${driver.lastName}`,
      ownerId: driver.id,
      url: [ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.DRIVERS, driver.id].join('/'),
    }));
  }, [schedule?.drivers]);

  const handleEventClick = async (args: EventClickArg) => {
    const { event } = args;
    const { rideId, bookingNumber, itemType } = event.extendedProps;

    switch (itemType) {
      case ScheduleItemType.RIDE:
        if (
          !bookingNumber ||
          !rideId ||
          [UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(permissions?.ORDER)
        )
          return;
        try {
          setBookingItem({ bookingNumber, rideId });
          setIsOrderDetailsModalOpen(true);
        } catch (err) {
          toast.error(t('Toast.failure'));
        }
        break;
      case ScheduleItemType.VACATION:
      case ScheduleItemType.TIME_OFF:
      case ScheduleItemType.SICKNESS:
        setIsVacationModalOpen(true);
        setIsVacationEditMode(true);
        setSelectedCellInfo({
          ...args,
          ...args.event,
          start: dayjs(args?.event?._instance?.range.start).utc() as unknown as Date,
          end: dayjs(args?.event?._instance?.range.end).utc() as unknown as Date,
          resource: {
            ...(args as unknown as DateSelectArg).resource,
            id: args.event.id,
            extendedProps: args.event._def.extendedProps,
          } as ResourceApi,
        } as unknown as DateSelectArg);
        break;
      default:
    }
  };

  const handleDateChange = async (args: DatesSetArg) => {
    const { startStr, endStr, view } = args;

    // We create this string match to validate if the request should be made
    // handleDateChange might be called multiple times with the same values
    // and this will lead to an infinite loop
    const part = `${startStr}${endStr}${view}`;
    if (part === selectedDateString) return;

    setQueryParams({
      start: dayjs(startStr).format('YYYY-MM-DD'),
      end: dayjs(endStr).format('YYYY-MM-DD'),
    });

    setSelectedDateString(part);
  };

  const handleSelectHeaderEvent = (option: AddEventTypeEnum) => {
    if (option === AddEventTypeEnum.VACATION) {
      setIsVacationModalOpen(true);
    }
  };

  const handleClearVacationModalState = () => {
    setIsVacationModalOpen(false);
    setIsVacationEditMode(false);
    setSelectedRange(undefined);
    setSelectedCellInfo(undefined);
  };

  const handleSelectRange = (range: DateSelectArg) => {
    if (range.jsEvent != null) {
      setSelectedRange({
        position: {
          left: range.jsEvent.clientX,
          top: range.jsEvent.clientY,
        },
        data: {
          start: range.start,
          end: range.end,
        },
      });
    }
    setSelectedCellInfo(range);
    setIsEventDropdownListOpen(true);
  };

  const getShowAddFirstDriverButton = () => {
    return (
      !schedule?.drivers?.length &&
      !isDataLoading &&
      hasWritePermission(permissions?.DRIVER_DETAILS)
    );
  };

  const getAddEventOptions = () => {
    const options = [];
    if (
      ![UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(permissions.DRIVER_SCHEDULE)
    ) {
      options.push(AddEventTypeEnum.VACATION);
    }
    return options;
  };

  const handleCloseEventDropdown = () => {
    setSelectedRange(undefined);
    setSelectedCellInfo(undefined);
    setSelectedDateString('');

    setIsEventDropdownListOpen(false);
    setIsVacationEditMode(false);
    setIsVacationModalOpen(false);

    setBookingItem(undefined);
  };

  const handleClearOrderDetailsModalState = () => {
    setIsOrderDetailsModalOpen(false);
    setBookingItem(undefined);
  };

  return (
    <>
      {bookingItem && (
        <WrappedOrderDetailsModal
          open={isOrderDetailsModalOpen}
          setOpen={setIsOrderDetailsModalOpen}
          onClose={handleClearOrderDetailsModalState}
          bookingNumber={bookingItem.bookingNumber}
          rideId={bookingItem.rideId}
        />
      )}
      <DriverCreateModalForm
        isModalOpen={isDriverModalOpen}
        setIsModalOpen={setIsDriverModalOpen}
      />
      <DriverVacationModal
        open={isVacationModalOpen}
        setOpen={setIsVacationModalOpen}
        onClose={handleClearVacationModalState}
        isEditMode={isVacationEditMode}
        defaultValues={{
          startTime: formatDateWithTz(selectedCellInfo?.start),
          endTime: formatDateWithTz(selectedCellInfo?.end),
          ownerId: selectedCellInfo?.resource?.extendedProps.ownerId ?? '',
          resourceId: selectedCellInfo ? Number(selectedCellInfo?.resource?.id) : undefined,
          itemType: selectedCellInfo
            ? selectedCellInfo.resource?.extendedProps.itemType
            : DEFAULT_VACATION_TYPE,
        }}
      />
      <AddEventDropdownList
        schedulerType={SchedulerTypeEnum.DRIVER_SCHEDULER}
        addEventOptions={getAddEventOptions()}
        open={isEventDropdownListOpen}
        handleClose={(exitWithoutSelection) => {
          // if exitWithoutSelection reset all selection state
          if (exitWithoutSelection) handleCloseEventDropdown();
          else setIsEventDropdownListOpen(false);
        }}
        handleEventClick={handleSelectHeaderEvent}
        anchorReference='anchorPosition'
        anchorPosition={selectedRange?.position}
      />
      {getShowAddFirstDriverButton() && (
        <SchedulerOverlay>
          <Button sx={{ marginTop: '100px' }} onClick={() => setIsDriverModalOpen(true)}>
            {t('SchedulerView.actions.addFirstDriver')}
          </Button>
        </SchedulerOverlay>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100%',
          position: 'relative',
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            mb: 1,
          }}
        >
          <AddEventDropdown
            schedulerType={SchedulerTypeEnum.DRIVER_SCHEDULER}
            handleEventClick={handleSelectHeaderEvent}
          />
          <SchedulerPageHeader calendarRef={calendarRef} />
        </Box>
        <CustomFullCalendar
          resources={elementSource as ResourceSourceInput}
          events={eventSource as EventSourceInput}
          schedulerType={SchedulerTypeEnum.DRIVER_SCHEDULER}
          calendarRef={calendarRef}
          handleEventClick={handleEventClick}
          handleDateChange={handleDateChange}
          handleSelectRange={handleSelectRange}
          calendarProps={{
            initialView: 'resourceTimelineTwoWeek',
            resourceAreaHeaderContent: t('SchedulerView.tabs.drivers'),
          }}
        />
      </Box>
    </>
  );
}

export default DriverSchedulerView;
